import React, {useCallback, useEffect, useState} from 'react';
import {Link, useSearchParams} from 'react-router-dom';
import moment from 'moment';
import API from '../../../utils/API';
import {API_ENDPOINTS} from '../../../apiConfig';
import routes from '../../../routesConfig';
import {deviceLabel, DAY_NAMES, formatMinutes, StatusBadge, todayIso} from './inquiryUtils';

const STATUS_DOT = {
    new: 'border-l-amber-400 bg-amber-50',
    confirmed: 'border-l-blue-500 bg-blue-50',
    converted: 'border-l-emerald-500 bg-emerald-50',
    no_show: 'border-l-red-400 bg-red-50 opacity-70',
    cancelled: 'border-l-gray-300 bg-gray-50 opacity-60 line-through'
};

const mondayOf = (iso) => moment(iso, 'YYYY-MM-DD').startOf('isoWeek').format('YYYY-MM-DD');

/** O víkendu rovnou ukázat nadcházející týden. */
const defaultWeek = () => {
    const today = moment();
    return (today.isoWeekday() >= 6 ? today.add(1, 'week') : today).format('YYYY-MM-DD');
};

/** Týdenní přehled rezervací a obsazenosti servisu. */
const InquiryCalendar = () => {
    const [params, setParams] = useSearchParams();
    const from = mondayOf(params.get('od') || defaultWeek());
    const [data, setData] = useState(null);
    const [showCancelled, setShowCancelled] = useState(false);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        try {
            const res = await API.get(API_ENDPOINTS.webInquiry.calendar, {params: {from, days: 7}, meta: {silentError: true}});
            setData(res.data);
            setError(null);
        } catch (e) {
            setError(e);
        }
    }, [from]);

    useEffect(() => {
        load();
    }, [load]);

    const shift = (weeks) => setParams({od: moment(from).add(weeks, 'weeks').format('YYYY-MM-DD')}, {replace: true});
    const today = todayIso();

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h1 className="text-xl font-semibold">Kalendář rezervací</h1>
                    <p className="text-sm text-gray-600">
                        Termíny předání zařízení. Kapacitu blokují jen potvrzené rezervace.{' '}
                        <Link to={routes.admin.inquiries.settingsFull} className="text-indigo-700 hover:underline">Otevírací doba a počet lidí</Link>
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button type="button" onClick={() => shift(-1)} className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm hover:bg-gray-50">← Předchozí</button>
                    <button type="button" onClick={() => setParams({}, {replace: true})} className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm hover:bg-gray-50">Tento týden</button>
                    <button type="button" onClick={() => shift(1)} className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm hover:bg-gray-50">Další →</button>
                </div>
            </div>

            <div className="flex flex-wrap items-center gap-4 text-xs text-gray-600">
                <span className="font-medium text-gray-800">
                    {moment(from).format('D. M.')} – {moment(from).add(6, 'days').format('D. M. YYYY')}
                </span>
                <Legend className={STATUS_DOT.new} label="Čeká na potvrzení"/>
                <Legend className={STATUS_DOT.confirmed} label="Potvrzeno"/>
                <Legend className={STATUS_DOT.converted} label="Převzato"/>
                <label className="ml-auto flex items-center gap-1.5">
                    <input type="checkbox" checked={showCancelled} onChange={(event) => setShowCancelled(event.target.checked)}/>
                    Zobrazit zrušené a nedorazivší
                </label>
            </div>

            {error ? <div className="text-sm text-red-600">Kalendář se nepodařilo načíst.</div> : null}

            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-7">
                {(data?.days || []).map((day) => {
                    const items = (data.reservations || [])
                        .filter((item) => item.reservation?.date === day.date)
                        .filter((item) => showCancelled || !['cancelled', 'no_show'].includes(item.status));
                    const usage = day.capacity ? Math.min(100, Math.round((day.used / day.capacity) * 100)) : 0;
                    const isPast = day.date < today;
                    return (
                        <section
                            key={day.date}
                            className={`flex min-h-[10rem] flex-col rounded-lg border bg-white p-3 ${day.date === today ? 'border-indigo-400 ring-1 ring-indigo-200' : 'border-gray-200'} ${isPast ? 'opacity-70' : ''}`}
                        >
                            <header className="mb-2">
                                <div className="flex items-baseline justify-between">
                                    <span className="font-semibold">{DAY_NAMES[day.weekday]}</span>
                                    <span className="text-sm text-gray-500">{moment(day.date).format('D. M.')}</span>
                                </div>
                                {day.closed ? (
                                    <div className="text-xs text-red-600">Zavřeno{day.note ? ` – ${day.note}` : ''}</div>
                                ) : (
                                    <>
                                        <div className="text-xs text-gray-500">
                                            {day.open}–{day.close} · {day.technicians} {day.technicians === 1 ? 'člověk' : day.technicians < 5 ? 'lidé' : 'lidí'}
                                            {day.note ? ` · ${day.note}` : ''}
                                        </div>
                                        <div className="mt-1.5 h-1.5 overflow-hidden rounded bg-gray-100" title={`Obsazeno ${formatMinutes(day.used)} z ${formatMinutes(day.capacity)}`}>
                                            <div className={`h-full ${usage >= 90 ? 'bg-red-500' : usage >= 60 ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{width: `${usage}%`}}/>
                                        </div>
                                        <div className="mt-0.5 text-[11px] text-gray-500">volno {formatMinutes(day.remaining)}</div>
                                    </>
                                )}
                            </header>
                            <ul className="space-y-1.5">
                                {items.map((item) => (
                                    <li key={item._id}>
                                        <Link
                                            to={routes.admin.getInquiry(item._id)}
                                            className={`block rounded border-l-4 px-2 py-1.5 text-xs hover:brightness-95 ${STATUS_DOT[item.status] || 'border-l-gray-300 bg-gray-50'}`}
                                        >
                                            <span className="flex items-center justify-between gap-1">
                                                <strong className="text-sm">{item.reservation?.time}</strong>
                                                {item.status === 'new' ? <StatusBadge status="new" type="reservation" className="!px-1 !text-[10px]"/> : null}
                                            </span>
                                            <span className="block truncate font-medium">{item.contact?.name}</span>
                                            <span className="block truncate text-gray-600">{deviceLabel(item)}</span>
                                            <span className="block text-gray-500">{formatMinutes(item.reservation?.minutes)}</span>
                                        </Link>
                                    </li>
                                ))}
                                {!items.length && !day.closed ? <li className="text-xs text-gray-400">Žádné rezervace</li> : null}
                            </ul>
                        </section>
                    );
                })}
            </div>
        </div>
    );
};

const Legend = ({className, label}) => (
    <span className="flex items-center gap-1.5">
        <span className={`inline-block h-3 w-3 rounded-sm border-l-4 ${className}`}/>
        {label}
    </span>
);

export default InquiryCalendar;
